import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { intlShape, injectIntl } from '../../util/reactIntl';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import { ensureListing, ensureTransaction } from '../../util/data';
import { dateFromAPIToLocalNoon } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';
import { txIsPaymentPending } from '../../util/transaction';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { NamedRedirect } from '../../components';

import {
  acceptSale,
  declineSale,
  loadData,
  setInitialValues,
  sendMessage,
  sendReview,
  fetchMoreMessages,
} from './TransactionPage.duck';
import css from './TransactionPage.css';

import { formatDateDisplay, formatSmartDateTime } from '../../util/dateDisplay';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faArrowCircleLeft, faLink } from '@fortawesome/free-solid-svg-icons';

import DefaultProfile from '../../assets/default_profile.png';
import { getProviderDisplayInfo } from './transactionPageHelpers';

import ContainerWrapper from '../../custom-components/wrappers/container-wrapper/ContainerWrapper';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import Footer from '../../custom-components/elements/footer/Footer';

import NavBarWrapper from '../../custom-components/wrappers/navbar-wrapper/NavBarWrapper';
import NavBar from '../../custom-components/elements/nav-bar/NavBar';
import MessageBubble from '../../custom-components/elements/message-bubble/MessageBubble';
import ProfileCard from '../../custom-components/elements/profile-card/ProfileCard';
import StatusBlock from './StatusBlock/StatusBlock';

import BookingModal from '../../custom-components/elements/listing-profile-right/bookingModal/BookingModal';
import SkyLight from 'react-skylight';
import IconSpinner from '../../components/IconSpinner/IconSpinner';

import DefaultListing from '../../assets/default-listing.jpg';
import MainDashboardWrapper from '../../components/MainDashboardWrapper/MainDashboardWrapper';

const PROVIDER = 'provider';
const CUSTOMER = 'customer';

export const TransactionPageComponent = props => {
  const {
    currentUser,
    fetchMessagesInProgress,
    fetchTransactionError,
    history,
    messages,
    onSendMessage,
    params,
    sendMessageInProgress,
    transaction,
    transactionRole,
    onAcceptSale,
    onDeclineSale,
    callSetInitialValues,
    onInitializeCardPaymentData,
  } = props;

  const [messageInput, setMessageInput] = useState('');

  const currentTransaction = ensureTransaction(transaction);
  const currentListing = ensureListing(currentTransaction.listing);
  const isProviderRole = transactionRole === PROVIDER;
  const isCustomerRole = transactionRole === CUSTOMER;

  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const transactionId = currentTransaction && currentTransaction.id && currentTransaction.id.uuid;
  const customerName =
    (currentTransaction &&
      currentTransaction.customer &&
      currentTransaction.customer.attributes &&
      currentTransaction.customer.attributes.profile &&
      currentTransaction.customer.attributes.profile.displayName) ||
    'this customer';
  const providerInfo = getProviderDisplayInfo(currentTransaction);

  const getTimeAgo = isoDate => {
    if (!isoDate) return null;
    const diffMs = Date.now() - new Date(isoDate).getTime();
    if (diffMs < 60000) return 'just now';
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) return minutes + 'm ago';
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours + 'h ago';
    const days = Math.floor(hours / 24);
    return days + 'd ago';
  };
  const timeAgoText =
    currentTransaction &&
    currentTransaction.attributes &&
    getTimeAgo(currentTransaction.attributes.createdAt);

  const myUserId = currentUser && currentUser.id && currentUser.id.uuid;
  const firstMessage = messages && messages.length > 0 ? messages[0] : null;
  const firstMessageSenderId =
    firstMessage && firstMessage.sender && firstMessage.sender.id && firstMessage.sender.id.uuid;
  const isFirstMessageFromCustomer = !!(firstMessage && firstMessageSenderId !== myUserId);
  const inquiryText =
    isFirstMessageFromCustomer &&
    firstMessage.attributes &&
    firstMessage.attributes.content;
  const showInquiryExcerptBlock =
    isProviderRole &&
    currentTransaction &&
    currentTransaction.attributes &&
    currentTransaction.attributes.lastTransition === 'transition/confirm-payment';

  const redirectToCheckoutPageWithInitialValues = (initialValues, listing) => {
    const routes = routeConfiguration();
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
    callSetInitialValues(setInitialValues, initialValues);
    onInitializeCardPaymentData();
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { id: currentListing.id.uuid, slug: createSlug(currentListing.attributes.title) },
        {}
      )
    );
  };

  if (
    txIsPaymentPending(currentTransaction) &&
    isCustomerRole &&
    currentTransaction.attributes.lineItems
  ) {
    const currentBooking = ensureListing(currentTransaction.booking);

    const initialValues = {
      listing: currentListing,
      transaction: currentTransaction,
      bookingData: {},
      bookingDates: {
        bookingStart: dateFromAPIToLocalNoon(currentBooking.attributes.start),
        bookingEnd: dateFromAPIToLocalNoon(currentBooking.attributes.end),
      },
    };

    redirectToCheckoutPageWithInitialValues(initialValues, currentListing);
  }

  const handleSubmitBookingRequest = values => {
    const { ...bookingData } = values;

    const bookingDates = {
      startDate: new Date(Date.UTC(2025, 7, 14)),
      endDate: new Date(Date.UTC(2025, 7, 16)),
    };

    const initialValues = {
      listing: currentListing,
      transaction: currentTransaction,
      bookingData,
      bookingDates: {
        bookingStart: bookingDates.startDate,
        bookingEnd: bookingDates.endDate,
      },
      confirmPaymentError: null,
    };

    redirectToCheckoutPageWithInitialValues(initialValues, currentListing);
  };

  const isDataAvailable =
    currentUser &&
    currentTransaction.id &&
    currentTransaction.id.uuid === params.id &&
    currentTransaction.attributes.lineItems &&
    currentTransaction.customer &&
    currentTransaction.provider &&
    !fetchTransactionError;

  const isOwnSale =
    isDataAvailable &&
    isProviderRole &&
    currentUser.id.uuid === currentTransaction.provider.id.uuid;
  const isOwnOrder =
    isDataAvailable &&
    isCustomerRole &&
    currentUser.id.uuid === currentTransaction.customer.id.uuid;

  if (isDataAvailable && isProviderRole && !isOwnSale) {
    console.error('Tried to access a sale that was not owned by the current user');
    return <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />;
  } else if (isDataAvailable && isCustomerRole && !isOwnOrder) {
    console.error('Tried to access an order that was not owned by the current user');
    return <NamedRedirect name="InboxPage" params={{ tab: 'orders' }} />;
  }

  const isSentMessage = message => {
    const myID = currentUser && currentUser.id.uuid;
    const messageSenderId = message && message.sender && message.sender.id.uuid;
    return myID === messageSenderId;
  };

  const onSubmitMessage = () => {
    const txId = currentTransaction && currentTransaction.id && currentTransaction.id.uuid;
    if (messageInput.length > 0) {
      onSendMessage(txId, messageInput);
      setMessageInput('');
    }
  };

  let simpleDialog;
  const bookNow = () => {
    const isHourly =
      currentListing.attributes.publicData && currentListing.attributes.publicData.per === 'hourly';
    if (isHourly) {
      simpleDialog.show();
    } else {
      handleSubmitBookingRequest({ quantity: 1 });
    }
  };

  const getStatusBlock = status => {
    const isTransactionAvailable =
      currentTransaction && currentTransaction.id && currentTransaction.attributes.lineItems;

    // Line items are looked up by `code`, not fixed array position — the
    // array only has 2 entries (order, commission) when no coupon is
    // applied, but 3 (order, discount, commission) when one is, so a fixed
    // index (e.g. lineItems[1] as "the fee") points at the wrong item as
    // soon as a discount is present.
    const lineItems = isTransactionAvailable ? currentTransaction.attributes.lineItems : [];
    const orderLineItem = lineItems.find(li => li.code === 'line-item/units');
    const discountLineItem = lineItems.find(li => li.code === 'line-item/coupon-discount');
    const minimumTopUpLineItem = lineItems.find(
      li => li.code === 'line-item/minimum-price-adjustment'
    );
    const commissionLineItem = lineItems.find(li => li.code === 'line-item/provider-commission');

    const units = orderLineItem ? orderLineItem.quantity.d[0] : null;
    const subtotalAmount = orderLineItem ? orderLineItem.lineTotal.amount / 100 : 0;
    const discountAmount = discountLineItem ? Math.abs(discountLineItem.lineTotal.amount) / 100 : 0;
    const minimumTopUpAmount = minimumTopUpLineItem
      ? Math.abs(minimumTopUpLineItem.lineTotal.amount) / 100
      : 0;
    const feeAmount = commissionLineItem ? Math.abs(commissionLineItem.lineTotal.amount) / 100 : 0;
    const isMinimumFee = commissionLineItem ? !commissionLineItem.percentage : false;
    const makeAmount = isTransactionAvailable
      ? currentTransaction.attributes.payoutTotal.amount / 100
      : 0;

    if (isProviderRole) {
      switch (status) {
        case 'transition/confirm-payment':
          return (
            <div>
              <StatusBlock
                statusQuote="Customer is waiting for your response."
                units={units}
                subtotalAmount={subtotalAmount}
                discountAmount={discountAmount}
                minimumTopUpAmount={minimumTopUpAmount}
                feeAmount={feeAmount}
                isMinimumFee={isMinimumFee}
                makeAmount={makeAmount}
              />
              <div className={css.pendingButtons}>
                <p onClick={() => setShowAcceptModal(true)}>Accept</p>
                <p onClick={() => setShowDeclineModal(true)}>Decline</p>
              </div>
            </div>
          );
        case 'transition/accept':
          return (
            <div>
              <StatusBlock
                statusQuote="You have accepted the transaction."
                units={units}
                subtotalAmount={subtotalAmount}
                discountAmount={discountAmount}
                minimumTopUpAmount={minimumTopUpAmount}
                feeAmount={feeAmount}
                isMinimumFee={isMinimumFee}
                makeAmount={makeAmount}
              />
              <div className={css.pendingButtons}></div>
            </div>
          );
        case 'transition/expire':
          return (
            <div>
              <StatusBlock
                statusQuote="The transaction has expired."
                units={units}
                subtotalAmount={subtotalAmount}
                discountAmount={discountAmount}
                minimumTopUpAmount={minimumTopUpAmount}
                feeAmount={feeAmount}
                isMinimumFee={isMinimumFee}
                makeAmount={makeAmount}
              />
              <div className={css.pendingButtons}></div>
            </div>
          );
        case 'transition/decline':
          return (
            <div>
              <StatusBlock
                statusQuote="You have declined the transaction."
                units={units}
                subtotalAmount={subtotalAmount}
                discountAmount={discountAmount}
                minimumTopUpAmount={minimumTopUpAmount}
                feeAmount={feeAmount}
                isMinimumFee={isMinimumFee}
                makeAmount={makeAmount}
              />
              <div className={css.pendingButtons}></div>
            </div>
          );
        case 'transition/enquire':
          break;
        case 'transition/complete':
          return (
            <div>
              <StatusBlock
                statusQuote="You have completed the transaction."
                units={units}
                subtotalAmount={subtotalAmount}
                discountAmount={discountAmount}
                minimumTopUpAmount={minimumTopUpAmount}
                feeAmount={feeAmount}
                isMinimumFee={isMinimumFee}
                makeAmount={makeAmount}
              />
              <div className={css.pendingButtons}></div>
            </div>
          );
        default:
          return <IconSpinner />;
      }
    }
  };

  return (
    <PageWrapper>
      <MainDashboardWrapper title={'Inquiries'} page={'coach-inquiry'}>
        <SkyLight
          hideOnOverlayClicked
          ref={ref => (simpleDialog = ref)}
          className={css.model}
          dialogStyles={{
            width: 'min(440px, calc(100vw - 40px))',
            minHeight: 'auto',
            top: '50%',
            left: '50%',
            marginTop: '-230px',
            marginLeft: 'calc(-1 * min(220px, calc(50vw - 20px)))',
            padding: 0,
            borderRadius: '20px',
            overflow: 'hidden',
            backgroundColor: '#fff',
            boxShadow:
              '0 25px 60px -12px rgba(20, 184, 166, 0.35), 0 0 0 1px rgba(20, 184, 166, 0.08)',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
          overlayStyles={{
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
          closeButtonStyle={{ display: 'none' }}
        >
          <BookingModal
            currentListing={currentListing}
            handleBookingSubmit={handleSubmitBookingRequest}
            onClose={() => simpleDialog.hide()}
          />
        </SkyLight>
        <div className={css.base}>
          <div className={css.chat}>
            <div className={css.mBack} onClick={() => props.history.goBack()}>
              <FontAwesomeIcon className={css.icon} icon={faArrowCircleLeft} />
              <p>Back</p>
            </div>
            <div>
              <p className={css.title}>
                {isProviderRole
                  ? currentTransaction &&
                    currentTransaction.customer &&
                    currentTransaction.customer.attributes.profile.displayName
                  : 'You'}{' '}
                enquired about
              </p>
              <p
                onClick={() =>
                  props.history.push(`/l/${currentListing.id && currentListing.id.uuid}`)
                }
                className={css.listName}
              >
                {currentListing && currentListing.attributes.title}
              </p>
              {!isProviderRole && providerInfo.id && (
                <div
                  className={css.providerRow}
                  onClick={() => props.history.push(`/u/${providerInfo.id}`)}
                >
                  <img
                    className={css.providerAvatar}
                    alt={providerInfo.name}
                    src={providerInfo.imageUrl}
                  />
                  <span className={css.providerName}>{providerInfo.name}</span>
                </div>
              )}
              <p />
              <p className={css.textDescription}>
                <span>Waiting for service provider to accept.</span>
              </p>
              <p className={css.textDescription}>
                <span>You can send a message to the service provider below.</span>
              </p>
              <p />
              <p className={css.mDate}>
                {currentTransaction &&
                  currentTransaction.attributes.createdAt &&
                  formatDateDisplay(currentTransaction.attributes.createdAt)}
                {timeAgoText && (
                  <span className={css.timeAgoBadge}> &middot; Sent {timeAgoText}</span>
                )}
              </p>
            </div>
            {showInquiryExcerptBlock && (
              <div className={css.inquiryExcerpt}>
                {inquiryText ? (
                  <>
                    <h4 className={css.inquiryExcerptLabel}>Inquiry message</h4>
                    <p className={css.inquiryExcerptText}>"{inquiryText}"</p>
                  </>
                ) : (
                  <>
                    <h4 className={css.inquiryExcerptLabel}>No inquiry message</h4>
                    <p className={css.inquiryExcerptEmpty}>
                      {customerName} did not include a message. Consider sending one
                      to introduce yourself or ask a clarifying question before
                      accepting.
                    </p>
                  </>
                )}
              </div>
            )}
            <div className={css.messageBubbles}>
              {fetchMessagesInProgress && <IconSpinner />}
              {messages &&
                currentUser &&
                messages.map((item, index) => {
                  const profileImgUrl = item.sender.profileImage
                    ? item.sender.profileImage.attributes.variants['square-small'].url
                    : DefaultProfile;
                  return (
                    <MessageBubble
                      key={index}
                      isSentBubble={isSentMessage(item)}
                      imgUrl={profileImgUrl}
                      message={item.attributes.content}
                      time={formatSmartDateTime(item.attributes.createdAt)}
                    />
                  );
                })}
            </div>
            <div>
              <div className={css.messageInput}>
                <input
                  value={messageInput}
                  onChange={e => setMessageInput(e.target.value)}
                  onKeyPress={e => { if (e.key === 'Enter') onSubmitMessage(); }}
                  className={css.mInput}
                  type="text"
                  placeholder="Type a message..."
                />
                <div
                  onClick={onSubmitMessage}
                  onTouchEnd={(e) => { e.preventDefault(); onSubmitMessage(); }}
                  className={css.sendButton}
                  role="button"
                  aria-label="Send message"
                >
                  {sendMessageInProgress ? (
                    <IconSpinner className={css.sendSpinner} />
                  ) : (
                    <FontAwesomeIcon className={css.icon} icon={faPaperPlane} />
                  )}
                </div>
              </div>
              <div className={css.line}></div>
            </div>
          </div>
          <div className={css.rightSection}>
            <div>
              {getStatusBlock(
                currentTransaction.id && currentTransaction.attributes.lastTransition
              )}
            </div>
            <ProfileCard
              isDark={false}
              price={currentListing.id && currentListing.attributes.price.amount / 100}
              rating={0}
              title={
                currentListing.id &&
                currentListing.attributes.title &&
                currentListing.attributes.title.length >= 50
                  ? currentListing.attributes.title.substring(0, 50) + '...'
                  : currentListing.attributes.title
              }
              desc={
                currentListing.id &&
                currentListing.attributes.description &&
                currentListing.attributes.description.length >= 200
                  ? currentListing.attributes.description.substring(0, 200) + '...'
                  : currentListing.attributes.description
              }
              imgUrl={
                (currentListing.id &&
                  currentListing.images &&
                  currentListing.images.length > 0 &&
                  currentListing.images[0].attributes.variants['landscape-crop'].url) ||
                DefaultListing
              }
              dashboardEvents={[
                {
                  name: 'View',
                  icon: faLink,
                  onClick: () => {
                    props.history.push(`/l/${currentListing.id && currentListing.id.uuid}`);
                  },
                },
              ]}
            />

            {currentTransaction.id &&
              currentTransaction.attributes.lastTransition === 'transition/enquire' &&
              isCustomerRole && (
                <p onClick={e => bookNow()} className={css.bookNowButton}>
                  Book now
                </p>
              )}
          </div>
        </div>

        {showAcceptModal && (
          <div
            className={css.modalOverlay}
            onClick={() => setShowAcceptModal(false)}
            role="presentation"
          >
            <div className={css.modalCard} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3 className={css.modalTitle}>Accept this inquiry?</h3>
              <p className={css.modalBody}>
                {customerName} will be notified that you have accepted, and the
                booking will appear in your sessions list. Payment is captured
                only once the customer confirms the booking time.
              </p>
              <div className={css.modalActions}>
                <button
                  type="button"
                  className={css.modalCancel}
                  onClick={() => setShowAcceptModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={css.modalPrimary}
                  onClick={() => {
                    onAcceptSale(transactionId);
                    setShowAcceptModal(false);
                  }}
                >
                  Yes, Accept
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeclineModal && (
          <div
            className={css.modalOverlay}
            onClick={() => {
              setShowDeclineModal(false);
              setDeclineReason('');
            }}
            role="presentation"
          >
            <div className={css.modalCard} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3 className={css.modalTitle}>Decline this inquiry?</h3>
              <p className={css.modalBody}>
                {customerName} will be notified that their inquiry was declined.
                This cannot be undone.
              </p>
              <label className={css.modalLabel} htmlFor="decline-reason">
                Reason (optional)
              </label>
              <select
                id="decline-reason"
                className={css.modalSelect}
                value={declineReason}
                onChange={e => setDeclineReason(e.target.value)}
              >
                <option value="">No reason</option>
                <option value="not-fit">Not the right fit</option>
                <option value="capacity">At capacity right now</option>
                <option value="timing">Wrong timing</option>
                <option value="info">Need more info first</option>
                <option value="other">Other</option>
              </select>
              <div className={css.modalActions}>
                <button
                  type="button"
                  className={css.modalCancel}
                  onClick={() => {
                    setShowDeclineModal(false);
                    setDeclineReason('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={css.modalDestructive}
                  onClick={() => {
                    onDeclineSale(transactionId);
                    setShowDeclineModal(false);
                    setDeclineReason('');
                  }}
                >
                  Decline Inquiry
                </button>
              </div>
            </div>
          </div>
        )}
      </MainDashboardWrapper>
    </PageWrapper>
  );
};

TransactionPageComponent.defaultProps = {
  currentUser: null,
  fetchTransactionError: null,
  acceptSaleError: null,
  declineSaleError: null,
  transaction: null,
  fetchMessagesError: null,
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
};

const { bool, func, oneOf, shape, string, arrayOf, number } = PropTypes;

TransactionPageComponent.propTypes = {
  params: shape({ id: string }).isRequired,
  transactionRole: oneOf([PROVIDER, CUSTOMER]).isRequired,
  currentUser: propTypes.currentUser,
  fetchTransactionError: propTypes.error,
  acceptSaleError: propTypes.error,
  declineSaleError: propTypes.error,
  acceptInProgress: bool.isRequired,
  declineInProgress: bool.isRequired,
  onAcceptSale: func.isRequired,
  onDeclineSale: func.isRequired,
  scrollingDisabled: bool.isRequired,
  transaction: propTypes.transaction,
  fetchMessagesError: propTypes.error,
  totalMessagePages: number.isRequired,
  oldestMessagePageFetched: number.isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  initialMessageFailedToTransaction: propTypes.uuid,
  savePaymentMethodFailed: bool,
  sendMessageInProgress: bool.isRequired,
  sendMessageError: propTypes.error,
  onShowMoreMessages: func.isRequired,
  onSendMessage: func.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  callSetInitialValues: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,

  history: shape({ push: func.isRequired }).isRequired,
  location: shape({ search: string }).isRequired,

  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    fetchTransactionError,
    acceptSaleError,
    declineSaleError,
    acceptInProgress,
    declineInProgress,
    transactionRef,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
  } = state.TransactionPage;
  const { currentUser } = state.user;

  const transactions = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);
  const transaction = transactions.length > 0 ? transactions[0] : null;

  return {
    currentUser,
    fetchTransactionError,
    acceptSaleError,
    declineSaleError,
    acceptInProgress,
    declineInProgress,
    scrollingDisabled: isScrollingDisabled(state),
    transaction,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onAcceptSale: transactionId => dispatch(acceptSale(transactionId)),
    onDeclineSale: transactionId => dispatch(declineSale(transactionId)),
    onShowMoreMessages: txId => dispatch(fetchMoreMessages(txId)),
    onSendMessage: (txId, message) => dispatch(sendMessage(txId, message)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onSendReview: (role, tx, reviewRating, reviewContent) =>
      dispatch(sendReview(role, tx, reviewRating, reviewContent)),
    callSetInitialValues: (setInitialValues, values) => dispatch(setInitialValues(values)),
    onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  };
};

const TransactionPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(TransactionPageComponent);

TransactionPage.loadData = loadData;
TransactionPage.setInitialValues = setInitialValues;

export default TransactionPage;
